/**
 * #2101 — the incoming-message chime, the volume, and the flashing.
 *
 * Ed, 2026-10-20: "we need a sound to accommodate the incoming message with a
 * volume control and flash on/off."
 *
 * ⚠ PER PERSON, PER BROWSER — NOT A CLIENT SETTING AND NOT A SERVER ONE. A
 * Client Admin does not choose whether their crew's laptops make a noise, and
 * the same person wants sound on a dispatch desk and silence on a phone in a
 * theatre. So these live in `localStorage` and never leave the browser: there is
 * nothing to sync, nothing to migrate, and no new column.
 *
 * ⚠ EVERY STORAGE CALL IS WRAPPED. `localStorage` is not merely absent in
 * private modes — the ACCESSOR ITSELF THROWS in a Safari private window and
 * under "block all cookies", before any read happens. An unguarded
 * `localStorage.getItem` there takes the whole chat panel down on mount, which
 * is a worse outcome than losing a volume preference. Defaults are returned on
 * any failure and a write that fails is simply forgotten.
 *
 * Pure except for the two obvious effects, and those take their globals as
 * arguments so this file is testable under node — which is the point, because
 * the mistakes here (a chime for your own message, a title left flashing after
 * the tab is focused, a clamp that lets volume go negative) are all silent.
 */

const KEY = 'cs3-chat-notify';

/** Sound on at mid volume, flash on — #2101 item 5 states the defaults. */
export const DEFAULTS = Object.freeze({ sound: true, volume: 0.5, flash: true });

/** How many times the tab title and the launcher blink before giving up. */
export const FLASH_LIMIT = 30;

/** The blink interval, slow enough to read the real title between flashes. */
export const FLASH_MS = 1200;

function clampVolume(raw) {
    const n = Number(raw);
    if (!isFinite(n)) return DEFAULTS.volume;

    return Math.min(1, Math.max(0, n));
}

/**
 * Read the preferences, falling back to the defaults for anything missing,
 * malformed, or unreadable.
 *
 * ⚠ FIELD BY FIELD, NOT ALL-OR-NOTHING. A stored object from a future version
 * with one unknown key, or one key of the wrong type, must not throw away the
 * other two settings — the user would see their volume reset because something
 * unrelated changed shape.
 */
export function readPrefs(storage) {
    const store = storage || safeStorage();
    let raw = null;

    try {
        raw = store ? store.getItem(KEY) : null;
    } catch (e) {
        return Object.assign({}, DEFAULTS);
    }

    if (!raw) return Object.assign({}, DEFAULTS);

    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        // Corrupt value (a half-written string, someone's devtools). Defaults,
        // and the next save overwrites it.
        return Object.assign({}, DEFAULTS);
    }

    const p = (parsed && typeof parsed === 'object') ? parsed : {};

    return {
        sound: typeof p.sound === 'boolean' ? p.sound : DEFAULTS.sound,
        volume: p.volume === undefined ? DEFAULTS.volume : clampVolume(p.volume),
        flash: typeof p.flash === 'boolean' ? p.flash : DEFAULTS.flash,
    };
}

/**
 * Persist the preferences. Returns whether it stuck, so a caller can tell —
 * but nothing in the panel treats a failure as an error: the setting still
 * applies for this page, it just will not be there next time.
 */
export function writePrefs(prefs, storage) {
    const store = storage || safeStorage();
    if (!store) return false;

    const clean = {
        sound: (prefs || {}).sound !== false,
        volume: clampVolume((prefs || {}).volume),
        flash: (prefs || {}).flash !== false,
    };

    try {
        store.setItem(KEY, JSON.stringify(clean));

        return true;
    } catch (e) {
        // Quota exceeded, or a private window that allows the accessor and
        // refuses the write.
        return false;
    }
}

/**
 * `window.localStorage` if it can be touched at all, else null.
 *
 * The try/catch is around the PROPERTY ACCESS, not around a method call, for
 * the reason in the file header.
 */
export function safeStorage(win) {
    const w = win || (typeof window === 'undefined' ? null : window);
    if (!w) return null;

    try {
        return w.localStorage || null;
    } catch (e) {
        return null;
    }
}

/**
 * Should an arriving message make a noise?
 *
 * ⚠ NEVER FOR YOUR OWN MESSAGE, and the test is the SENDER's uid rather than
 * the display name: two people in a local can share a name, and a chime for
 * your own post is the kind of small wrongness that makes a feature feel
 * broken. This mirrors `chatPanel.onChannelDoc`, which already decides the
 * toast the same way — one rule, asked twice.
 *
 * ⚠ AND NOT ON THE FIRST DOCUMENT OF A CHANNEL. Firestore sends the current
 * state of every channel document when a listener attaches, so on every page
 * load each channel reports its last message as "new"
 * (`applyChannelDoc` compares against a `lastMessageId` that is undefined until
 * then). Without `primed`, signing in to eight channels would play eight chimes
 * and start the title flashing, every single time anyone loaded a CS3 page.
 */
export function shouldChime(prefs, { mine, looking, primed }) {
    if (!primed) return false;
    if (mine) return false;
    // Already reading that channel: a chime would be beeping at someone who is
    // watching the message arrive. The toast suppresses itself here for the
    // same reason.
    if (looking) return false;

    return (prefs || {}).sound !== false;
}

/** Same question for the flashing, which follows its own switch. */
export function shouldFlash(prefs, { mine, looking, primed }) {
    if (!primed) return false;
    if (mine) return false;
    if (looking) return false;

    return (prefs || {}).flash !== false;
}

/**
 * The chime.
 *
 * ⚠ GENERATED, NOT A FILE. A short sine blip from WebAudio costs no asset, no
 * extra request, no bundle weight and no decision about where static audio
 * lives in this build — and it cannot 404 on a tenant subdomain, which an
 * `<audio src>` under `/static` demonstrably can.
 *
 * ⚠ BROWSERS BLOCK AUDIO UNTIL THE PAGE HAS HAD A USER GESTURE, and #2101 item
 * 6 says fail quietly when that happens. So every call is wrapped and a
 * blocked or unavailable context is simply a silent chime — the flash still
 * works, which is the point of having both.
 */
export function createChime(win) {
    const w = win || (typeof window === 'undefined' ? null : window);
    const Ctx = w && (w.AudioContext || w.webkitAudioContext);
    let ctx = null;

    return {
        /** Returns true when a sound was actually started. */
        play(volume) {
            if (!Ctx) return false;

            try {
                if (!ctx) ctx = new Ctx();
                // Autoplay policy: a context created before any gesture starts
                // 'suspended'. resume() is a promise that rejects when it is
                // still not allowed — caught, not reported.
                if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
                    const r = ctx.resume();
                    if (r && typeof r.catch === 'function') r.catch(() => {});
                }

                const gain = ctx.createGain();
                const osc = ctx.createOscillator();
                const level = Math.min(1, Math.max(0, Number(volume)));

                osc.type = 'sine';
                // Two notes, a fifth apart: recognisable as a notification at
                // low volume without being a siren at high.
                osc.frequency.setValueAtTime(880, ctx.currentTime);
                osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.09);

                // A short envelope rather than a square on/off, which clicks.
                // Scaled well below 1: a raw gain of 1 on a sine is unpleasantly
                // loud on laptop speakers, so "max volume" is 0.25 of full
                // scale and the slider spans something usable.
                gain.gain.setValueAtTime(0.0001, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, level * 0.25), ctx.currentTime + 0.01);
                gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);

                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start();
                osc.stop(ctx.currentTime + 0.24);

                return true;
            } catch (e) {
                // No WebAudio, a blocked context, or a browser that threw on
                // one of the node calls. Quietly nothing.
                return false;
            }
        },
        /** Let go of the context when the panel is destroyed. */
        close() {
            try {
                if (ctx && typeof ctx.close === 'function') ctx.close();
            } catch (e) { /* already gone */ }
            ctx = null;
        },
    };
}

/**
 * The flashing tab title.
 *
 * ⚠ IT RESTORES THE TITLE IT FOUND, and that is the whole difficulty. CS3
 * rewrites `document.title` on navigation, so capturing it once at mount and
 * restoring that forever would put a stale page name in the tab. The real title
 * is re-read every time flashing STARTS, and `stop()` puts back whatever was
 * captured then — so a navigation during a flash is the one case that loses,
 * and it loses by one title rather than permanently.
 *
 * ⚠ AND IT GIVES UP. `FLASH_LIMIT` blinks and it stops on its own: a tab left
 * open overnight with an unread message must not blink until the battery dies,
 * and an interval nothing cancels is how that happens.
 */
export function createTitleFlasher(doc, timers) {
    const d = doc || (typeof document === 'undefined' ? null : document);
    const t = timers || (typeof window === 'undefined' ? null : window);
    const setI = (t && t.setInterval) ? t.setInterval.bind(t) : null;
    const clearI = (t && t.clearInterval) ? t.clearInterval.bind(t) : null;

    let timer = null;
    let original = null;
    let on = false;
    let blinks = 0;

    function paint(text) {
        try { d.title = text; } catch (e) { /* nothing to do about it */ }
    }

    /*
     * Declared as a function rather than written as a method on the returned
     * object, so the self-stop at FLASH_LIMIT does not depend on `this` inside
     * a callback — the one way this could break is a caller doing
     * `const { stop } = flasher`, and it should not matter whether they do.
     */
    function stop() {
        if (timer === null) return;
        if (clearI) clearI(timer);
        timer = null;
        if (original !== null) paint(original);
        original = null;
        on = false;
        blinks = 0;
    }

    function start(label) {
        if (!d || !setI) return false;
        if (timer !== null) return true;      // already flashing; do not re-capture

        original = d.title;
        blinks = 0;
        on = false;

        timer = setI(() => {
            on = !on;
            paint(on ? (label || 'New message') : original);
            blinks += 1;
            if (blinks >= FLASH_LIMIT) stop();
        }, FLASH_MS);

        return true;
    }

    return {
        get flashing() { return timer !== null; },
        start,
        stop,
    };
}
